import { Link } from 'react-router-dom';

export default function CaseStudySection() {
  const timeline = [
    {
      phase: 'Phase 1',
      title: 'Property Selection & Initial Assessment',
      duration: 'Week 1-2',
      description: 'Found a 2-bedroom apartment in Ota City, Tokyo. Conducted feasibility study including zoning verification, building compliance check, and revenue projection analysis.',
      actions: [
        'Verified property is in Special Zone for National Strategic Special Zones',
        'Confirmed building owner consent possibility',
        'Calculated estimated monthly revenue: ¥280,000-350,000',
      ],
      outcome: 'Decision to proceed with minpaku registration',
      icon: 'ri-search-line',
    },
    {
      phase: 'Phase 2',
      title: 'Document Preparation & Application',
      duration: 'Week 3-6',
      description: 'Gathered all required documents including building plans, fire safety certificates, and owner consent forms. Prepared comprehensive application package for Ota City office.',
      actions: [
        'Obtained building blueprints and fire equipment inspection certificate',
        'Secured written consent from building owner and management company',
        'Prepared detailed operation plan and emergency contact system',
      ],
      outcome: 'Application submitted to Ota City Tourism Division',
      icon: 'ri-file-list-3-line',
    },
    {
      phase: 'Phase 3',
      title: 'Administrative Review & Approval',
      duration: 'Week 7-10',
      description: 'Responded to inquiries from Ota City officials. Conducted on-site inspection with fire department. Addressed all compliance requirements and received official approval.',
      actions: [
        'Coordinated on-site inspection with city officials',
        'Installed additional fire safety equipment as requested',
        'Submitted supplementary documentation for clarification',
      ],
      outcome: 'Minpaku registration certificate issued (Registration #大田区特民第XXX号)',
      icon: 'ri-government-line',
    },
    {
      phase: 'Phase 4',
      title: 'Airbnb Setup & Listing Optimization',
      duration: 'Week 11-12',
      description: 'Created professional Airbnb listing with high-quality photos, optimized description, and competitive pricing strategy. Set up automated messaging and booking management system.',
      actions: [
        'Professional photography session capturing all rooms and amenities',
        'Wrote compelling listing description in English and Japanese',
        'Implemented dynamic pricing based on Tokyo market demand',
      ],
      outcome: 'Listing went live with instant booking enabled',
      icon: 'ri-airplay-line',
    },
    {
      phase: 'Phase 5',
      title: 'First Guest & Operations Launch',
      duration: 'Week 13-14',
      description: 'Received first booking within 3 days of listing. Successfully hosted first international guest from Australia. Established smooth check-in/check-out process and cleaning coordination.',
      actions: [
        'Coordinated seamless self-check-in with smart lock system',
        'Provided 24/7 multilingual guest support',
        'Arranged professional cleaning after checkout',
      ],
      outcome: 'Received 5-star review: "Perfect traditional Japanese experience in Tokyo!"',
      icon: 'ri-user-star-line',
    },
    {
      phase: 'Phase 6',
      title: 'Revenue Stabilization & Growth',
      duration: 'Month 4-6',
      description: 'Achieved consistent 75% occupancy rate. Monthly revenue stabilized at ¥320,000 average. Implemented guest feedback improvements and seasonal pricing adjustments.',
      actions: [
        'Optimized pricing for peak seasons (cherry blossom, autumn)',
        'Added amenities based on guest feedback (portable WiFi, travel guides)',
        'Built strong review profile with 4.9+ average rating',
      ],
      outcome: 'Property became Superhost status with steady passive income',
      icon: 'ri-line-chart-line',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            From Application to Operation
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            A real case study of launching a successful Airbnb property in Ota City, Tokyo. 
            This timeline shows the actual journey from property selection to profitable operation.
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-teal-500 via-teal-400 to-teal-500"></div>

          {/* Timeline items */}
          <div className="space-y-16">
            {timeline.map((item, index) => (
              <div
                key={index}
                className={`relative flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content card */}
                <div className="w-full lg:w-5/12">
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-sm font-semibold text-teal-600 bg-teal-50 px-3 py-1 rounded-full">
                        {item.phase}
                      </span>
                      <span className="text-sm text-gray-500">{item.duration}</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {item.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-teal-600"></i>
                        Key Actions
                      </h4>
                      <ul className="space-y-2">
                        {item.actions.map((action, idx) => (
                          <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-teal-500 mt-1">•</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-teal-50 rounded-lg p-4 border-l-4 border-teal-500">
                      <p className="text-sm font-semibold text-gray-900 mb-1">Outcome</p>
                      <p className="text-sm text-gray-700">{item.outcome}</p>
                    </div>
                  </div>
                </div>

                {/* Center icon */}
                <div className="hidden lg:flex w-2/12 justify-center">
                  <div className="relative z-10 w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                </div>

                {/* Mobile icon */}
                <div className="lg:hidden w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block w-5/12"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <div className="mt-20 bg-gradient-to-r from-teal-600 to-teal-500 rounded-2xl p-12 text-white">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-3">Final Results After 6 Months</h3>
            <p className="text-teal-50 text-lg">
              From zero to profitable Airbnb business in Ota City, Tokyo
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">75%</div>
              <div className="text-teal-50">Average Occupancy Rate</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">¥320K</div>
              <div className="text-teal-50">Monthly Revenue</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">4.9</div>
              <div className="text-teal-50">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-teal-50">Compliance Maintained</div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#F5F1E8] to-white p-8 md:p-12 rounded-lg border-2 border-[#B8860B]/30 shadow-lg">
          <h3 className="text-2xl md:text-3xl font-bold text-[#8B4513] mb-4">
            Start Your Success Story Today
          </h3>
          <p className="text-gray-700 mb-8 max-w-2xl mx-auto leading-relaxed">
            Let us guide you through the complete journey—from <Link to="/minpaku-registration-tokyo" className="text-[#B8860B] hover:text-[#8B4513] font-semibold underline">minpaku registration in Tokyo</Link> to profitable operations with our <Link to="/airbnb-management-tokyo" className="text-[#B8860B] hover:text-[#8B4513] font-semibold underline">Airbnb management Tokyo</Link> services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/tokyo-airbnb-support#contact"
              className="px-8 py-4 bg-gradient-to-r from-[#8B4513] to-[#A0522D] text-white font-semibold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              Schedule Free Consultation
            </Link>
            <Link
              to="/minpaku-license-support"
              className="px-8 py-4 bg-white text-[#8B4513] font-semibold rounded-lg border-2 border-[#B8860B] hover:bg-[#F5F1E8] hover:shadow-lg transition-all duration-300 whitespace-nowrap"
            >
              Learn About Licensing
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}